import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import {
  AiAgentsNamespaceGetWebsiteSourceArgs,
  AiAgentWebsiteSource,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { LggOptionsDropdownButtonWithCustomTriggerControlled } from 'src/components/general/button/dropdown-button';
import { DrawerOption } from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { LggDrawer } from 'src/components/general/drawer/lgg-drawer';
import { Icon } from 'src/components/general/icon';
import { LoadingSpinner } from 'src/components/general/inputs/shared';
import { MoreOptionsIcon } from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { VerticalDivider } from 'src/components/layout/vertical-divider';
import { getDeleteRowOption } from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { formatBytes } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/helpers';
import { WEBSITE_SOURCE_DETAIL_QUERY } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/queries';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useFetchMoreWithErrorHandling } from 'src/hooks/use-fetch-more-with-error-handling';
import { useFormatDate } from 'src/hooks/use-format-date';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const LOAD_MORE_THRESHOLD_PX = 40;

type WebsiteSourceDetailVariables = AiAgentsNamespaceGetWebsiteSourceArgs & {
  after?: string;
};

const DetailsDrawer = styled(LggDrawer)`
  .ant-drawer-header,
  .ant-drawer-body {
    border: none;
    padding: 0;
  }

  .ant-drawer-mask {
    background: none;
  }

  &&& .ant-drawer-content-wrapper {
    box-shadow: 0 14px 51px ${({ theme }) => theme.colors.carbon}24;
  }
`;

const DrawerHeader = styled(FlexRow)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.koala};
  gap: 10px;
  height: 80px;
  padding: 0 20px;
`;

const DrawerTitle = styled.span`
  color: ${({ theme }) => theme.colors.carbon};
  flex: 1;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 20px;
  letter-spacing: -0.4px;
  line-height: 25px;
`;

const HeaderActions = styled(FlexRow)`
  align-items: center;
  gap: 14px;
`;

const TitleIcon = styled(Icon)`
  svg {
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const HeaderMoreOptionsIcon = styled(MoreOptionsIcon)`
  left: 0;
`;

const CloseIcon = styled(Icon)`
  cursor: pointer;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

const StatSeparator = styled(VerticalDivider)`
  background-color: ${({ theme }) => theme.colors.casper};
  margin: 0 24px;
  opacity: 0.5;
`;

const LoadingContainer = styled(FlexColumn)`
  align-items: center;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const Body = styled(FlexColumn)`
  gap: 15px;
  height: 100%;
  overflow-y: auto;
  padding: 20px;
`;

const SectionTitle = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
`;

const ExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.gogo};
  font-family: ${({ theme }) => theme.font.medium};
  overflow-wrap: anywhere;
`;

const Stats = styled(FlexRow)`
  align-items: center;
  background: ${({ theme }) => theme.colors.porcelain};
  border-radius: 6px;
  min-height: 52px;
  padding: 0 20px;
`;

const Stat = styled(FlexColumn)`
  flex: 1;
  gap: 1px;
  line-height: 15px;
  white-space: nowrap;
`;

const StatLabel = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 11px;
`;

const StatValue = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-size: 12px;
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.colors.casper};
  height: 1px;
  opacity: 0.5;
`;

const PagesList = styled(FlexColumn)`
  font-size: 13px;
  gap: 14px;
  line-height: 18px;
`;

const PageRow = styled(FlexRow)`
  gap: 10px;
  justify-content: space-between;
`;

const PageSize = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.medium};
  white-space: nowrap;
`;

type WebsiteDetailsBodyProps = {
  website: AiAgentWebsiteSource;
  onLoadMore: (after: string) => void;
};

const WebsiteDetailsBody = memo<WebsiteDetailsBodyProps>(({ website, onLoadMore }) => {
  const { t } = useTranslation(['aiAgents']);
  const { formatDate } = useFormatDate();
  const isSinglePage = website.mode === 'SINGLE_PAGE';
  const { hasNextPage, endCursor } = website.pages.pageInfo;
  const lastSync = website.lastSyncedAt
    ? formatDate(website.lastSyncedAt, 'D MMM, YYYY')
    : t('aiAgents:knowledge.websites.details.neverSynced');

  return (
    <Body
      onScroll={(event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isNearEnd =
          scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX;

        if (hasNextPage && endCursor && isNearEnd) {
          onLoadMore(endCursor);
        }
      }}
    >
      <SectionTitle>
        {t('aiAgents:knowledge.websites.details.rootUrl')}{' '}
        <ExternalLink href={website.url} target="_blank" rel="noopener noreferrer">
          {website.url}
        </ExternalLink>
      </SectionTitle>
      <Stats>
        <Stat>
          <StatLabel>{t('aiAgents:knowledge.websites.details.lastSync')}</StatLabel>
          <StatValue data-lgg-id="website-details-last-sync">{lastSync}</StatValue>
        </Stat>
        <StatSeparator />
        <Stat>
          <StatLabel>{t('aiAgents:knowledge.websites.details.maxPages')}</StatLabel>
          <StatValue data-lgg-id="website-details-max-pages">
            {isSinglePage ? 1 : website.maxPages}
          </StatValue>
        </Stat>
        <StatSeparator />
        <Stat>
          <StatLabel>{t('aiAgents:knowledge.websites.details.crawlDepth')}</StatLabel>
          <StatValue data-lgg-id="website-details-crawl-depth">
            {isSinglePage ? 1 : website.crawlDepth}
          </StatValue>
        </Stat>
      </Stats>
      <Divider />
      <SectionTitle>
        {t('aiAgents:knowledge.websites.details.importedPages')}
      </SectionTitle>
      <PagesList>
        {website.pages.edges.map(({ node }) => (
          <PageRow key={node.id} data-lgg-id="website-details-page">
            <ExternalLink href={node.url} target="_blank" rel="noopener noreferrer">
              {node.url}
            </ExternalLink>
            {typeof node.size === 'number' && (
              <PageSize>{formatBytes(node.size)}</PageSize>
            )}
          </PageRow>
        ))}
      </PagesList>
    </Body>
  );
});

type WebsiteDetailsDrawerProps = {
  websiteId: string;
  onClose: VoidFunction;
  onEdit: (website: AiAgentWebsiteSource) => void;
  onDelete: (website: AiAgentWebsiteSource) => void;
};

export const WebsiteDetailsDrawer = memo<WebsiteDetailsDrawerProps>(
  ({ websiteId, onClose, onEdit, onDelete }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const { id: companyId } = useCurrentInstitution();
    const { hasPermission } = useAuthorization();
    const handleGraphQLError = useHandleGraphQLError();

    const [loadingMore, setLoadingMore] = useState(false);

    const { data, fetchMore } = useQuery<
      Pick<Query, 'aiAgents'>,
      WebsiteSourceDetailVariables
    >(WEBSITE_SOURCE_DETAIL_QUERY, {
      variables: { input: { companyId, id: websiteId } },
      onError: handleGraphQLError,
    });
    const fetchMoreWithErrorHandling = useFetchMoreWithErrorHandling<
      Pick<Query, 'aiAgents'>,
      WebsiteSourceDetailVariables
    >(fetchMore);

    const loadMore = (after: string) => {
      if (loadingMore) {
        return;
      }

      setLoadingMore(true);
      void fetchMoreWithErrorHandling({ variables: { after } }).finally(() =>
        setLoadingMore(false),
      );
    };

    const website = data?.aiAgents.getWebsiteSource;
    const options: DrawerOption[] = [];

    if (
      website?.mode === 'WEBSITE' &&
      hasPermission('ai:company.ai_agent_knowledge_base:update:all')
    ) {
      options.push({
        label: t('common:edit'),
        icon: 'edit',
        'data-lgg-id': 'website-option-edit',
        onClick: () => onEdit(website),
      });
    }

    if (website && hasPermission('ai:company.ai_agent_knowledge_base:delete:all')) {
      options.push(
        getDeleteRowOption({
          label: t('common:delete'),
          testId: 'website-option-delete',
          onClick: () => onDelete(website),
        }),
      );
    }

    const optionsMenu = options.length > 0 && (
      <LggOptionsDropdownButtonWithCustomTriggerControlled
        customTrigger={
          <HeaderMoreOptionsIcon
            type="moreOptions"
            lggTestId="website-details-more-options"
          />
        }
        customDropdownProps={{ getPopupContainer: (trigger) => trigger }}
        options={options}
      />
    );

    return (
      <DetailsDrawer
        visible
        width={375}
        placement="right"
        closable={false}
        onClose={onClose}
        data-lgg-id="website-details-drawer"
        title={
          <DrawerHeader>
            <TitleIcon type="world" />
            <DrawerTitle>{t('aiAgents:knowledge.websites.details.title')}</DrawerTitle>
            <HeaderActions>
              {optionsMenu}
              <CloseIcon type="close" lggTestId="drawer-close-icon" onClick={onClose} />
            </HeaderActions>
          </DrawerHeader>
        }
      >
        {website ? (
          <WebsiteDetailsBody website={website} onLoadMore={loadMore} />
        ) : (
          <LoadingContainer>
            <LoadingSpinner size="40px" />
          </LoadingContainer>
        )}
      </DetailsDrawer>
    );
  },
);
